import React, { useEffect, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { SquareArrowOutUpRightIcon } from 'lucide-react'

import { dayjs } from '@/lib/dayjs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CalendarEvent } from '@/features/calendar/types'
import { DocumentAttributeRender } from '@/features/documents/document-attribute-render'

export interface EventComponentProps {
  event: CalendarEvent
  children: React.ReactNode
}

export const EventComponent: React.FC<EventComponentProps> = ({
  event,
  children,
}) => {
  const searchParams = useSearch({ from: '/_authenticated/calendar/' }) ?? {}
  const { eventId } = searchParams
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (eventId === event.id) {
      setIsOpen(true)
    }
  }, [event.id, eventId])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4">
          <p className="text-muted-foreground mb-1 text-sm">
            {dayjs(event.start_date).format('D MMMM YYYY')}
            {dayjs(event.start_date).isAfter(event.end_date, 'day') &&
              ` - ${dayjs(event.end_date).format('D MMMM YYYY')}`}
          </p>
          <h3 className="mb-4 text-sm font-semibold">
            <Link
              to="/documents/$documentId"
              params={{ documentId: event.document_attribute.document_id }}
            >
              {event.title}{' '}
              <SquareArrowOutUpRightIcon className="inline size-4" />
            </Link>
          </h3>

          {event.document_attribute && (
            <p className="text-muted-foreground mb-2 text-sm">
              {event.document_attribute.attribute.name}:{' '}
              <span className="text-primary">
                <DocumentAttributeRender
                  attributeJsonSchema={
                    event.document_attribute.attribute.json_schema
                  }
                  value={event.document_attribute.value}
                />
              </span>
            </p>
          )}

          {event.description && (
            <p className="mt-2 text-sm">{event.description}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
